import { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Star, Users, TrendingUp, MapPin, Clock, ChevronLeft, ChevronRight, Flame, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { AuthContext } from '@/App';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const to12h = (t) => {
  if (!t) return t;
  const [h, m] = t.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return t;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hr = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hr}:${String(m).padStart(2, '0')} ${ampm}`;
};

const rankMedals = ['', '\u{1F947}', '\u{1F948}', '\u{1F949}'];

const BarFeedCard = ({ bar, rank, stats, onCheckin }) => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [checkingIn, setCheckingIn] = useState(false);

  const photos = (bar.photos || []).length > 0
    ? bar.photos
    : bar.image_url ? [bar.image_url] : [];

  const resolvePhotoUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('/api')) return `${BACKEND_URL}${url}`;
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  const isTop3 = rank >= 1 && rank <= 3;
  const checkins = stats?.total_checkins || 0;
  const ratio = stats?.ratio || 'N/A';
  const females = stats?.female_count || 0;
  const males = stats?.male_count || 0;

  const getVibe = () => {
    if (checkins >= 10) return { label: 'Packed', color: 'bg-red-500/20 text-red-400 border-red-500/40' };
    if (checkins >= 5) return { label: 'Lively', color: 'bg-orange-500/20 text-orange-400 border-orange-500/40' };
    if (checkins >= 1) return { label: 'Warming Up', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40' };
    return { label: 'Chill', color: 'bg-slate-500/20 text-slate-400 border-slate-500/40' };
  };

  const vibe = getVibe();
  const specials = bar.drink_specials || [];
  const happyHours = bar.happy_hours || [];
  const promos = bar.promoted_posts || [];

  return (
    <Card
      data-testid={`bar-feed-card-${bar.id}`}
      className={`glass overflow-hidden cursor-pointer transition-all hover:scale-[1.01] ${
        isTop3
          ? 'border-2 border-amber-500/40 shadow-lg shadow-amber-500/10'
          : 'border border-slate-700/50 hover:border-indigo-500/40'
      }`}
      onClick={() => navigate(`/bar/${bar.id}`)}
    >
      {/* Photo Carousel */}
      <div className="relative w-full aspect-[16/9] bg-slate-800 overflow-hidden">
        {photos.length > 0 ? (
          <img
            src={resolvePhotoUrl(photos[photoIdx])}
            alt={bar.name}
            className="w-full h-full object-cover"
            onError={(e) => { e.target.src = '/nightbuzz-logo.png'; e.target.className = 'w-full h-full object-contain p-8 opacity-30'; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <img src="/nightbuzz-logo.png" alt="" className="h-16 opacity-20" />
          </div>
        )}

        {/* Photo nav */}
        {photos.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); setPhotoIdx(i => i === 0 ? photos.length - 1 : i - 1); }}
              className="absolute left-1 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/50 flex items-center justify-center text-white/80 hover:text-white"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setPhotoIdx(i => i === photos.length - 1 ? 0 : i + 1); }}
              className="absolute right-1 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/50 flex items-center justify-center text-white/80 hover:text-white"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
              {photos.map((_, i) => (
                <div key={i} className={`w-1.5 h-1.5 rounded-full ${i === photoIdx ? 'bg-white' : 'bg-white/40'}`} />
              ))}
            </div>
          </>
        )}

        {/* Rank Badge */}
        {isTop3 && (
          <div className="absolute top-2 left-2 text-3xl drop-shadow-lg">
            {rankMedals[rank]}
          </div>
        )}

        {/* Vibe Badge */}
        <Badge className={`absolute top-2 right-2 ${vibe.color} border text-xs font-medium`}>
          {checkins >= 10 && <Flame className="w-3 h-3 mr-0.5" />}
          {vibe.label}
        </Badge>

        {/* Ad badge if promoted */}
        {promos.length > 0 && (
          <Badge className="absolute bottom-2 right-2 bg-amber-500/80 text-black text-[10px]">Ad</Badge>
        )}
      </div>

      {/* Card Body */}
      <div className="p-4 space-y-2.5">
        {/* Bar Name + Rating */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="font-bold text-white text-lg truncate" style={{ fontFamily: 'Space Grotesk' }}>
              {isTop3 && <span className="mr-1">{rankMedals[rank]}</span>}
              {bar.name}
            </h3>
            {bar.address && (
              <p className="text-xs text-slate-400 flex items-center gap-1 truncate">
                <MapPin className="w-3 h-3 flex-shrink-0" />
                {bar.address.split(',')[0]}
              </p>
            )}
          </div>
          {bar.rating && (
            <div className="flex items-center gap-1 text-amber-400 shrink-0">
              <Star className="w-4 h-4 fill-amber-400" />
              <span className="font-semibold text-sm">{bar.rating}</span>
            </div>
          )}
        </div>

        {/* Stats Row: People, Ratio, Buzz, Check In */}
        <div className="flex items-center gap-3 text-sm">
          <div className="flex items-center gap-1">
            <Users className="w-4 h-4 text-indigo-400" />
            <span className="text-white font-medium">{checkins}</span>
          </div>
          {ratio !== 'N/A' && (
            <div className="flex items-center gap-1">
              <span className="text-pink-400 text-xs">&#9792;</span>
              <span className="text-white font-medium">{ratio}</span>
              <span className="text-blue-400 text-xs">&#9794;</span>
            </div>
          )}
          <div className="flex items-center gap-1">
            <TrendingUp className="w-3.5 h-3.5 text-green-400" />
            <span className="text-green-400 font-semibold text-xs">{(checkins * 6.5 + (females > males ? 10 : 0)).toFixed(0)}</span>
          </div>
          <button
            data-testid={`checkin-btn-${bar.id}`}
            onClick={async (e) => {
              e.stopPropagation();
              if (!user) { toast.error('Log in to check in'); return; }
              setCheckingIn(true);
              try {
                const token = localStorage.getItem('token');
                await axios.post(`${API}/checkins`, { bar_id: bar.id }, { headers: { Authorization: `Bearer ${token}` } });
                toast.success(`Checked in at ${bar.name}!`);
                if (onCheckin) onCheckin();
              } catch (err) {
                toast.error(err.response?.data?.detail || 'Check-in failed');
              } finally { setCheckingIn(false); }
            }}
            disabled={checkingIn}
            className="ml-auto flex items-center gap-1 px-2.5 py-1 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium transition-all disabled:opacity-50 shrink-0"
          >
            <LogIn className="w-3 h-3" />
            {checkingIn ? '...' : 'Check In'}
          </button>
        </div>

        {/* Drink Specials */}
        {specials.length > 0 && (
          <div className="space-y-1">
            <p className="text-amber-400 text-xs font-semibold uppercase tracking-wider">Specials</p>
            <div className="flex flex-wrap gap-1.5">
              {specials.slice(0, 3).map((s, i) => (
                <span key={i} className="text-xs bg-amber-500/10 text-amber-300 px-2 py-0.5 rounded-full border border-amber-500/20">
                  {s}
                </span>
              ))}
              {specials.length > 3 && (
                <span className="text-xs text-slate-500">+{specials.length - 3} more</span>
              )}
            </div>
          </div>
        )}

        {/* Happy Hour */}
        {happyHours.length > 0 && (
          <div className="flex items-center gap-2 p-2 bg-green-500/5 border border-green-500/20 rounded-lg">
            <Clock className="w-4 h-4 text-green-400 shrink-0" />
            <div className="min-w-0">
              <p className="text-green-400 text-xs font-semibold">Happy Hour</p>
              <p className="text-slate-300 text-xs truncate">
                {happyHours[0].description || `${to12h(happyHours[0].start_time)} - ${to12h(happyHours[0].end_time)}`}
              </p>
            </div>
          </div>
        )}

        {/* Promoted Post highlight */}
        {promos.length > 0 && (
          <div className="p-2 bg-amber-500/5 border border-amber-500/20 rounded-lg">
            <p className="text-amber-400 text-xs font-semibold">{promos[0].title}</p>
            <p className="text-slate-400 text-xs truncate">{promos[0].description}</p>
          </div>
        )}
      </div>
    </Card>
  );
};

export default BarFeedCard;
